<template>
  <v-dialog :model-value="modelValue" max-width="960" scrollable @update:model-value="emit('update:modelValue', $event)">
    <div class="ltcs-duplicate-dialog">
      <header class="ltcs-duplicate-dialog__header">
        <div class="header-title-wrap">
          <v-icon size="18" color="#c62828">mdi-content-duplicate</v-icon>
          <h3>중복 데이터 상세</h3>
        </div>
        <button type="button" class="close-btn" aria-label="닫기" @click="emit('update:modelValue', false)">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <div class="ltcs-duplicate-dialog__body">
        <LoadingOverlay :show="loading" text="중복 데이터 불러오는 중..." />

        <div v-if="!loading" class="duplicate-summary-bar">
          <span>
            {{ isClaimDetail ? '같은 방문이 여러 줄' : '동일 키 기준 중복' }}
            <button type="button" class="duplicate-count-link" @click="scrollToFirstGroup">
              {{ duplicateCount.toLocaleString() }}건
            </button>
            ({{ duplicateGroupCount.toLocaleString() }}개 그룹)
          </span>
          <span v-if="isClaimDetail" class="duplicate-hint duplicate-hint--claim">
            청구의뢰와 지급이 같이 있는 방문은 여기 나오지 않습니다.
            아래는 같은 방문이 같은 처리상태로 여러 줄인 경우입니다.
          </span>
          <span v-else class="duplicate-hint">아래 그룹별로 중복된 행을 확인할 수 있습니다.</span>
        </div>

        <div v-if="!loading && groups.length === 0" class="duplicate-empty">
          {{ isClaimDetail ? '같은 방문이 여러 줄인 중복은 없습니다.' : '중복 데이터가 없습니다.' }}
        </div>

        <div v-for="(group, index) in groups" :key="group.groupKey" class="duplicate-group">
          <button
            type="button"
            class="duplicate-group__toggle"
            :class="{ open: expandedKeys.has(group.groupKey) }"
            @click="toggleGroup(group.groupKey)"
          >
            <v-icon size="16">{{ expandedKeys.has(group.groupKey) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <span class="group-label">{{ group.label }}</span>
            <span class="group-count">{{ group.count }}건</span>
          </button>

          <div v-if="expandedKeys.has(group.groupKey)" class="duplicate-group__table-wrap">
            <table class="duplicate-group__table">
              <thead>
                <tr>
                  <th>번호</th>
                  <th v-for="col in displayColumns" :key="col.key">{{ col.title }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in group.rows" :key="row[idxKey]">
                  <td class="idx-cell">{{ row[idxKey] }}</td>
                  <td v-for="col in displayColumns" :key="col.key">{{ formatCell(row[col.key], col.key) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer class="ltcs-duplicate-dialog__footer">
        <v-btn variant="text" class="btn-close" @click="emit('update:modelValue', false)">닫기</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

export interface LtcsDuplicateGroup {
  groupKey: string;
  count: number;
  label: string;
  rows: Record<string, unknown>[];
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    loading?: boolean;
    duplicateCount?: number;
    duplicateGroupCount?: number;
    groups?: LtcsDuplicateGroup[];
    tableHeaders?: { title: string; key: string }[];
    idxKey?: string;
    sourceType?: string;
  }>(),
  {
    loading: false,
    duplicateCount: 0,
    duplicateGroupCount: 0,
    groups: () => [],
    tableHeaders: () => [],
    idxKey: 'idx',
    sourceType: '',
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const isClaimDetail = computed(
  () => props.sourceType === 'caion_ltcs_claim_detail'
);

const expandedKeys = ref(new Set<string>());

const displayColumns = computed(() => {
  if (props.tableHeaders.length <= 6) return props.tableHeaders;
  return props.tableHeaders.slice(0, 8);
});

const toggleGroup = (key: string) => {
  const next = new Set(expandedKeys.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedKeys.value = next;
};

const scrollToFirstGroup = () => {
  if (props.groups.length > 0) {
    expandedKeys.value = new Set([props.groups[0].groupKey]);
  }
};

const formatCell = (val: unknown, key: string) => {
  if (val == null || val === '') return '-';
  if (typeof key === 'string' && (key.endsWith('_date') || key.endsWith('_birth'))) {
    const d = new Date(String(val));
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
  }
  if (typeof key === 'string' && key.endsWith('_time')) {
    return String(val).substring(0, 5);
  }
  return String(val);
};

watch(
  () => props.modelValue,
  (open) => {
    if (open && props.groups.length > 0) {
      expandedKeys.value = new Set([props.groups[0].groupKey]);
    } else if (!open) {
      expandedKeys.value = new Set();
    }
  }
);
</script>
